import { createApi } from '@reduxjs/toolkit/query/react';

import { CollectFormsQuery } from '@/graphql/generated';
import { normalizeCollectFormsResponse } from '@/slices/indicators/indicators.normalizer';
import {
  CollectFormsQueryInput,
  CollectFormsResponse,
  ComputedIndicatorValueByOrganizationalUnit,
  EditIndicatorInput,
  Indicator,
  IndicatorCampaignProgress,
} from '@/slices/indicators/indicators.types';

import { symfonyBaseQuery } from '@/services/SymfonyClient';

type IndicatorsByIdsResponse = {
  indicators?: Indicator[];
};

type IndicatorByIdResponse = {
  indicator: Indicator | null;
};

type ClearIndicatorInput = {
  enterpriseId: string;
  indicatorId: string;
  organizationalUnitId?: string;
};

type DeleteIndicatorInput = {
  enterpriseId: string;
  indicatorId: string;
};

const indicatorsApi = createApi({
  baseQuery: symfonyBaseQuery({
    baseUrl: '/api/enterprises',
  }),
  reducerPath: 'indicators',
  tagTypes: ['Indicators', 'ConditionalIndicators', 'CollectForms'],
  endpoints: (build) => ({
    getCollectForms: build.query<CollectFormsQuery, CollectFormsQueryInput>({
      query: ({
        enterpriseId,
        page,
        limit,
        reportStandards,
        managerIds,
        relatedEntityIds,
        type,
        reportDatapointId,
        campaignId,
        searchValue,
        language,
      }) => ({
        url: `/${enterpriseId}/indicators/collect-forms`,
        method: 'GET',
        params: {
          page,
          limit,
          reportStandards,
          managerIds,
          relatedEntityIds,
          type,
          reportDatapointId,
          campaignId,
          searchValue,
          language,
        },
      }),
      transformResponse: (response: CollectFormsResponse) =>
        normalizeCollectFormsResponse(response),
      providesTags: (_result, _error, { enterpriseId }) => [
        { type: 'CollectForms', id: enterpriseId },
      ],
    }),
    getIndicatorsByIds: build.query<
      Indicator[],
      { enterpriseId: string; ids: string[] }
    >({
      query: ({ enterpriseId, ids }) => ({
        url: `/${enterpriseId}/indicators`,
        method: 'GET',
        params: {
          ids: ids.join(','),
        },
      }),
      transformResponse: (response: IndicatorsByIdsResponse) =>
        response.indicators ?? [],
      async onQueryStarted({ enterpriseId }, { dispatch, queryFulfilled }) {
        try {
          const { data: indicators } = await queryFulfilled;

          for (const indicator of indicators) {
            dispatch(
              indicatorsApi.util.upsertQueryData(
                'getIndicatorById',
                { enterpriseId, indicatorId: indicator.id },
                indicator,
              ),
            );
          }
        } catch {
          // Keep cache untouched when fetching fails.
        }
      },
      providesTags: (_result, _error, { ids }) =>
        ids.map((indicatorId) => ({
          type: 'Indicators',
          id: indicatorId,
        })),
    }),
    getIndicatorById: build.query<
      Indicator | null,
      { enterpriseId: string; indicatorId: string }
    >({
      query: ({ enterpriseId, indicatorId }) => ({
        url: `/${enterpriseId}/indicators/${indicatorId}`,
        method: 'GET',
      }),
      transformResponse: (response: IndicatorByIdResponse) =>
        response.indicator,
      providesTags: (_result, _error, { indicatorId }) => [
        { type: 'Indicators', id: indicatorId },
      ],
    }),
    getConditionalIndicators: build.query<
      Indicator[],
      { enterpriseId: string; reportId: string }
    >({
      query: ({ enterpriseId, reportId }) => ({
        url: `/${enterpriseId}/report/${reportId}/conditional-indicators`,
        method: 'GET',
      }),
      transformResponse: (response: IndicatorsByIdsResponse) =>
        response.indicators ?? [],
      providesTags: (_result, _error, { reportId }) => [
        { type: 'ConditionalIndicators', id: reportId },
      ],
    }),
    getIndicatorCampaigns: build.query<
      IndicatorCampaignProgress[],
      { enterpriseId: string; indicatorId: string }
    >({
      query: ({ enterpriseId, indicatorId }) => ({
        url: `/${enterpriseId}/indicators/${indicatorId}/campaigns`,
        method: 'GET',
      }),
      transformResponse: (response: {
        campaigns?: IndicatorCampaignProgress[];
      }) => response.campaigns ?? [],
      providesTags: (_result, _error, { indicatorId }) => [
        { type: 'Indicators', id: indicatorId },
      ],
    }),
    getIndicatorsCampaigns: build.query<
      Record<string, IndicatorCampaignProgress[]>,
      { enterpriseId: string; indicatorsIds: string[] }
    >({
      query: ({ enterpriseId, indicatorsIds }) => ({
        url: `/${enterpriseId}/indicators/campaigns`,
        method: 'GET',
        params: {
          indicatorsIds,
        },
      }),
      transformResponse: (response: {
        campaigns: Record<string, IndicatorCampaignProgress[]>;
      }) => response.campaigns,
      providesTags: (_result, _error, { indicatorsIds }) =>
        indicatorsIds.map((indicatorId) => ({
          type: 'Indicators',
          id: indicatorId,
        })),
      async onQueryStarted({ enterpriseId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          for (const indicatorId of Object.keys(data)) {
            dispatch(
              indicatorsApi.util.upsertQueryData(
                'getIndicatorCampaigns',
                { enterpriseId, indicatorId: indicatorId },
                data[indicatorId],
              ),
            );
          }
        } catch {
          // Ignore cache sync when list request fails.
        }
      },
    }),
    getComputedIndicatorValueByOrganizationalUnit: build.query<
      ComputedIndicatorValueByOrganizationalUnit,
      { enterpriseId: string; campaignId: string; indicatorId: string }
    >({
      query: ({ enterpriseId, campaignId, indicatorId }) => ({
        url: `/${enterpriseId}/campaigns/${campaignId}/indicators/${indicatorId}/computed-value`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { indicatorId }) => [
        { type: 'Indicators', id: indicatorId },
      ],
    }),
    editIndicator: build.mutation<
      Indicator,
      {
        enterpriseId: string;
        indicatorId: string;
        input: EditIndicatorInput;
      }
    >({
      query: ({ enterpriseId, indicatorId, input }) => ({
        url: `/${enterpriseId}/indicators/${indicatorId}`,
        method: 'PATCH',
        data: input,
      }),
      transformResponse: (response: IndicatorByIdResponse) => {
        if (!response.indicator) {
          throw new Error('Indicator not found after edit.');
        }

        return response.indicator;
      },
      async onQueryStarted(
        { enterpriseId, indicatorId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedIndicator } = await queryFulfilled;

          dispatch(
            indicatorsApi.util.upsertQueryData(
              'getIndicatorById',
              { enterpriseId, indicatorId },
              updatedIndicator,
            ),
          );

          dispatch(
            indicatorsApi.util.invalidateTags([
              { type: 'Indicators', id: indicatorId },
              { type: 'CollectForms', id: enterpriseId },
            ]),
          );
        } catch {
          // Keep cache untouched when update fails.
        }
      },
    }),
    clearIndicator: build.mutation<Indicator, ClearIndicatorInput>({
      query: ({ enterpriseId, indicatorId, organizationalUnitId }) => ({
        url: `/${enterpriseId}/indicators/${indicatorId}/clear`,
        method: 'POST',
        data:
          organizationalUnitId === undefined ? {} : { organizationalUnitId },
      }),
      transformResponse: (response: IndicatorByIdResponse) => {
        if (!response.indicator) {
          throw new Error('Indicator not found after clear.');
        }

        return response.indicator;
      },
      async onQueryStarted(
        { enterpriseId, indicatorId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data: updatedIndicator } = await queryFulfilled;

          dispatch(
            indicatorsApi.util.upsertQueryData(
              'getIndicatorById',
              { enterpriseId, indicatorId },
              updatedIndicator,
            ),
          );

          dispatch(
            indicatorsApi.util.invalidateTags([
              { type: 'Indicators', id: indicatorId },
              { type: 'CollectForms', id: enterpriseId },
            ]),
          );
        } catch {
          // Keep cache untouched when clear fails.
        }
      },
    }),
    deleteIndicator: build.mutation<void, DeleteIndicatorInput>({
      query: ({ enterpriseId, indicatorId }) => ({
        url: `/${enterpriseId}/indicators/${indicatorId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, error, { enterpriseId, indicatorId }) =>
        error
          ? []
          : [
              { type: 'Indicators', id: indicatorId },
              { type: 'CollectForms', id: enterpriseId },
            ],
    }),
  }),
});

export default indicatorsApi;
